// What the public website derives from a visit it saw arrive (ARCH-002
// amendment *Closed-vocabulary attribution and operator-registered links*,
// point 10). The result is one value of the domain's closed vocabulary — never
// the referring host, never a click identifier's value, and nothing is kept on
// the visitor's device: the class travels in the registration URL and nowhere
// else.

import {
  isAttributionLinkCode,
  isReferrerClass,
  type ReferrerClass,
} from '@amberon/crm-tenant-entitlements-domain';

/** What the browser knew when the visit arrived. */
export interface VisitFacts {
  /** The landing URL's query string (`location.search`). */
  readonly search: string;
  /** `document.referrer` — an empty string when the browser sent none. */
  readonly referrer: string;
  /** The site's own host, so a hop between our own pages is not read as a referral. */
  readonly host: string;
}

/**
 * The click identifiers an ad network appends to its own landing URLs. Only
 * their PRESENCE is read: the value identifies the click, so it never leaves
 * the landing URL.
 */
const PAID_SEARCH_CLICK_IDS = ['gclid', 'msclkid'] as const;
const PAID_SOCIAL_CLICK_IDS = ['fbclid'] as const;

/**
 * Search engines, recognised by their brand label so every country domain
 * counts as one engine: `google.de`, `www.google.co.uk` and `news.google.com`
 * are all Google.
 */
const SEARCH_BRANDS = [
  'google',
  'bing',
  'duckduckgo',
  'ecosia',
  'startpage',
  'qwant',
  'yahoo',
  'yandex',
  'baidu',
  'brave',
] as const;

/** Social networks, recognised by their brand label the same way. */
const SOCIAL_BRANDS = [
  'facebook',
  'instagram',
  'linkedin',
  'twitter',
  'youtube',
  'tiktok',
  'reddit',
  'threads',
  'pinterest',
  'xing',
  'whatsapp',
] as const;

/**
 * Social hosts matched in full, because their brand label is a single
 * character or a bare word that would match half the web (`x.com`, `t.co`).
 */
const SOCIAL_HOSTS = ['x.com', 'mobile.x.com', 't.co'] as const;

/** The referring host, lowercased and without its `www.` prefix, or an empty string. */
function hostnameOf(referrer: string): string {
  try {
    return new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    // A referrer the browser handed us in a shape we cannot parse says nothing.
    return '';
  }
}

/** The referrer class of a visit, always one of the closed vocabulary. */
export function classifyReferrer(visit: VisitFacts): ReferrerClass {
  const query = new URLSearchParams(visit.search);
  if (PAID_SEARCH_CLICK_IDS.some((id) => query.has(id))) return 'paid-search';
  if (PAID_SOCIAL_CLICK_IDS.some((id) => query.has(id))) return 'paid-social';

  const hostname = hostnameOf(visit.referrer);
  // A hop between our own pages tells us nothing new: whatever the visit was
  // worth travels in the URL from page to page, so a referral would overwrite
  // the truth with ourselves.
  if (!hostname || hostname === visit.host.toLowerCase().replace(/^www\./, '')) return 'direct';

  const labels = hostname.split('.');
  if (SEARCH_BRANDS.some((brand) => labels.includes(brand))) return 'organic-search';
  if (SOCIAL_BRANDS.some((brand) => labels.includes(brand))) return 'social';
  if ((SOCIAL_HOSTS as readonly string[]).includes(hostname)) return 'social';
  return 'referral';
}

/** What travels from page to page, and into the registration link. */
export interface VisitAttribution {
  /** The code of an operator-registered attribution link, when the page URL carried one. */
  readonly ref?: string;
  /** The referrer class — carried from the page URL when it holds one, derived otherwise. */
  readonly via: ReferrerClass;
}

/**
 * What this page knows about the visit. A value the page URL already carries
 * wins over a derived one, because it was derived on the page the visitor
 * actually arrived on; anything that fails its guard is dropped, never
 * corrected — the server validates the same way (ARCH-002 point 10).
 */
export function readVisitAttribution(visit: VisitFacts): VisitAttribution {
  const query = new URLSearchParams(visit.search);
  const ref = query.get('ref');
  const via = query.get('via');

  return {
    ...(isAttributionLinkCode(ref) ? { ref } : {}),
    via: isReferrerClass(via) ? via : classifyReferrer(visit),
  };
}
